/** Industry playbooks rendered at /seo-for/$industry. */
export type Industry = {
  slug: string;
  name: string;
  hero: string;
  intro: string;
  pain: string[];
  approach: { title: string; body: string }[];
  keywords: string[];
  faqs: { q: string; a: string }[];
};

export const INDUSTRIES: Industry[] = [
  {
    slug: "saas",
    name: "SaaS",
    hero: "SEO for SaaS — turn organic search into your cheapest CAC channel.",
    intro:
      "SaaS SEO is won on the bottom of the funnel: alternatives, vs, integrations, and use-case pages. I build topical clusters that capture intent at every stage and feed your free-trial pipeline.",
    pain: [
      "Marketing-led blog content that doesn't convert to trials",
      "Competitor pages outranking your homepage for branded \"vs\" queries",
      "Programmatic pages getting indexed but not ranked",
      "AI engines citing competitors instead of you",
    ],
    approach: [
      { title: "Bottom-of-funnel cluster", body: "Alternatives, comparisons, integrations, and use-case pages targeted at high-intent buyers." },
      { title: "Programmatic done right", body: "Quality-gated programmatic templates with unique data, schema, and editorial review — not thin doorway pages." },
      { title: "AEO for SaaS buyers", body: "FAQ + HowTo schema and citation-ready definitions so ChatGPT and Perplexity recommend you in product research prompts." },
      { title: "Product-led SEO", body: "Free tools, calculators, and templates that earn links and drive trial signups on autopilot." },
    ],
    keywords: [
      "SaaS SEO",
      "B2B SaaS SEO consultant",
      "SEO for software companies",
      "SaaS content strategy",
      "programmatic SEO SaaS",
    ],
    faqs: [
      { q: "How long does SaaS SEO take to drive trials?", a: "There is no fixed timeline. Bottom-of-funnel pages (alternatives, vs, integrations) are usually prioritised because they are closest to a trial decision; broader educational content tends to take longer. Trials from organic search are tracked monthly against a baseline." },
      { q: "Do you do programmatic SEO?", a: "Yes — but only when there's real underlying data and unique value per page. I'd rather ship 200 great programmatic pages than 20,000 thin ones." },
    ],
  },
  {
    slug: "ecommerce",
    name: "E-commerce",
    hero: "SEO for E-commerce — own category, product, and \"best\" queries.",
    intro:
      "E-commerce SEO compounds when collection pages, product detail pages, and editorial buying guides are wired together. I focus on revenue per session, not just rankings.",
    pain: [
      "Faceted navigation creating thousands of duplicate URLs",
      "Product pages with no unique copy and weak schema",
      "Collection pages outranked by affiliate listicles",
      "AI shopping answers citing competitors",
    ],
    approach: [
      { title: "Collection-first architecture", body: "Optimize the highest-traffic, highest-margin collection pages with editorial intros, internal links, and rich Product schema." },
      { title: "PDP optimization at scale", body: "Templated PDP improvements: unique copy frameworks, FAQ blocks, review schema, and AI-readable spec tables." },
      { title: "Buying-guide content", body: "\"Best X for Y\" articles that internally link to collection pages — the same playbook affiliate sites use, but on your domain." },
      { title: "Technical SEO for stores", body: "Faceted nav rules, Shopify/WooCommerce-specific fixes, Core Web Vitals, and canonical hygiene." },
    ],
    keywords: [
      "ecommerce SEO consultant",
      "Shopify SEO expert",
      "WooCommerce SEO",
      "product page SEO",
      "ecommerce SEO Bangladesh",
    ],
    faqs: [
      { q: "Do you work with Shopify and WooCommerce?", a: "Yes — both, plus BigCommerce and custom stacks. Shopify is my most-shipped platform." },
      { q: "Can SEO compete with paid ads on ROAS?", a: "On a 6–12 month horizon, organic almost always beats paid for repeatable category and \"best\" queries. The two work best together, not in opposition." },
    ],
  },
  {
    slug: "law-firms",
    name: "Law Firms",
    hero: "SEO for Law Firms — dominate \"near me\" and high-intent practice-area queries.",
    intro:
      "Legal SEO is the most competitive local vertical there is. Winning means surgical local SEO, deeply specific practice-area pages, and trust signals that satisfy YMYL standards.",
    pain: [
      "Generic practice-area pages that read like every other firm's",
      "Google Business Profile under-leveraged",
      "Competitor PPC eating organic clicks",
      "Thin author bios that fail E-E-A-T",
    ],
    approach: [
      { title: "Practice-area depth", body: "One detailed page per practice area per city, written for both Google and the prospective client researching their case at 11pm." },
      { title: "GBP + local citations", body: "Optimized Google Business Profile, weekly posts, and clean NAP across legal directories (Avvo, Justia, Martindale)." },
      { title: "Attorney E-E-A-T", body: "Detailed attorney bios with credentials, bar admissions, case results, and Person schema." },
      { title: "Review velocity", body: "A repeatable system for collecting reviews on Google, Avvo, and other legal-specific platforms." },
    ],
    keywords: [
      "law firm SEO",
      "SEO for lawyers",
      "attorney SEO consultant",
      "legal local SEO",
      "law firm marketing",
    ],
    faqs: [
      { q: "Is legal SEO worth it given the cost?", a: "It depends on your case values and how many new enquiries SEO brings in. Work out what one new client is worth to your firm, compare it with the monthly cost, and start with your highest-value practice areas so the return is easiest to measure." },
    ],
  },
  {
    slug: "healthcare",
    name: "Healthcare & Clinics",
    hero: "SEO for Healthcare — rank for high-intent local medical searches.",
    intro:
      "Healthcare SEO is YMYL territory: Google rewards expertise, authority, and trust harder here than anywhere else. I build content authored by clinicians, validated by schema, and surfaced via local SEO.",
    pain: [
      "Patient-facing content that violates Google's medical content guidelines",
      "Multi-location clinics with cannibalizing pages",
      "Slow, image-heavy sites failing Core Web Vitals",
      "GBP profiles missing services, photos, and review responses",
    ],
    approach: [
      { title: "Clinician-authored content", body: "Articles written or reviewed by your medical team, with Person schema, credentials, and last-reviewed dates." },
      { title: "Multi-location strategy", body: "City-level landing pages with unique provider info, services, and structured data for each location." },
      { title: "MedicalOrganization schema", body: "Full schema implementation including services, accepted insurance, hours, and patient reviews." },
      { title: "Patient-intent keyword map", body: "Symptom, condition, treatment, and \"near me\" queries mapped to the right page type." },
    ],
    keywords: [
      "healthcare SEO",
      "medical SEO",
      "clinic SEO consultant",
      "dental SEO",
      "multi-location healthcare SEO",
    ],
    faqs: [
      { q: "Do you handle HIPAA-aware analytics?", a: "Yes — I configure GA4 and Search Console setups that don't capture PHI, and document the data flow for your compliance team." },
    ],
  },
  {
    slug: "real-estate",
    name: "Real Estate",
    hero: "SEO for Real Estate — own neighborhood and listing-type queries.",
    intro:
      "Real estate SEO is hyperlocal. The win is owning every neighborhood, listing-type, and price-band query within your service area, plus a content engine that earns links.",
    pain: [
      "Listings that disappear from Google when sold",
      "Hyperlocal queries lost to Zillow and Realtor.com",
      "Generic agent bios with no E-E-A-T",
      "No long-form market reports earning links",
    ],
    approach: [
      { title: "Neighborhood pages", body: "Evergreen pages per neighborhood with market stats, schools, lifestyle data, and current listings — pages that survive when listings turn over." },
      { title: "Listing schema + post-sale strategy", body: "RealEstateListing schema and a 301 strategy for sold listings that preserves SEO equity." },
      { title: "Quarterly market reports", body: "Data-driven local market reports that earn local press links and rank for \"<city> housing market\" queries." },
      { title: "Agent E-E-A-T", body: "Detailed agent bios with sales history, neighborhoods served, and Person schema." },
    ],
    keywords: [
      "real estate SEO",
      "realtor SEO consultant",
      "real estate agent SEO",
      "real estate local SEO",
      "neighborhood SEO",
    ],
    faqs: [
      { q: "Can SEO compete with Zillow?", a: "Not on listing-type queries — but you can absolutely outrank them on neighborhood, market-report, and \"living in <city>\" content where local expertise matters." },
    ],
  },
  {
    slug: "restaurants",
    name: "Restaurants & Hospitality",
    hero: "SEO for Restaurants — fill seats with high-intent local searches.",
    intro:
      "Restaurant SEO is mostly Google Business Profile, backed by a supporting website. I run both — squeezing the GBP for every map-pack ranking and using the site to earn long-tail \"best <cuisine> in <city>\" traffic.",
    pain: [
      "GBP underused — no posts, missing menu, weak photos",
      "Menu trapped in a PDF or image, invisible to search",
      "No reviews flywheel — competitors out-reviewing 5:1",
      "Site failing Core Web Vitals on mobile",
    ],
    approach: [
      { title: "GBP as a marketing channel", body: "Weekly posts, full menu in GBP, dish-level photos, Q&A seeded, and review responses on every review." },
      { title: "Menu in HTML", body: "Server-rendered menu with Menu schema and dish-level pages where it makes sense." },
      { title: "Local content engine", body: "\"Best <cuisine>\", \"top <occasion>\", and neighborhood guides that link to your reservation page." },
      { title: "Review velocity system", body: "Post-meal review prompts via SMS/QR and a clear response cadence." },
    ],
    keywords: [
      "restaurant SEO",
      "restaurant local SEO",
      "Google Business Profile restaurant",
      "hospitality SEO",
      "cafe SEO",
    ],
    faqs: [
      { q: "How fast can I show up in the map pack?", a: "There is no guaranteed date. A complete Google Business Profile, steady genuine reviews and consistent name, address and phone details give you the best chance; how fast you move depends on your location and nearby competitors." },
    ],
  },
];